import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Send, Volume2, Video, BookOpen, ClipboardList, Trophy, MessageSquare, ChevronLeft, Lock, Sparkles, Award, Zap, X, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { notifySuccess, notifyError, notifyInfo } from '../../utils/toast';
import { handleAvatarError } from '../../utils/avatarHelper';
import PeerMateSidebar from './component/peermatesidebar';
import MasteryMap from './component/MasteryMap';
import InteractiveQuiz from './component/InteractiveQuiz';
import './component/Avatar.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const FREE_USAGE_KEY = 'peermate_free_usage_window';
const FREE_WINDOW_MS = 48 * 60 * 60 * 1000;
const FREE_DURATION_MS = 60 * 60 * 1000;

const MODE_MAP = 'map';
const MODE_TEACHING = 'teaching';
const MODE_ASSIGNMENT = 'assignment';
const MODE_QUIZ = 'quiz';

const formatMs = (ms) => {
  const t = Math.floor(Math.max(0, ms) / 1000);
  return `${Math.floor(t / 3600)}h ${Math.floor((t % 3600) / 60)}m ${t % 60}s`;
};

const parseInteractiveData = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};

// ─── Teaching Notes Panel ────────────────────────────────────────────────────
const TeachingPanel = ({ topic, onStartAssignment, userNote, onNoteChange, hydrating }) => {
  const idata = parseInteractiveData(topic?.interactive_data);
  const paragraphs = (topic?.content || '').split('\n\n').filter(Boolean);

  if (hydrating) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="font-black text-gray-900 uppercase tracking-widest text-sm">PeerMate is preparing your lesson...</p>
        <p className="text-xs text-gray-400">Generating high-fidelity academic content & assignments.</p>
      </div>
    );
  }

  return (
    <motion.div
      key="teaching"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="h-full flex flex-col gap-6"
    >
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-3 space-y-8">
        <div className="flex items-center gap-4">
          <div className="p-4 rounded-2xl bg-emerald-100 shadow-sm">
            <BookOpen size={24} className="text-emerald-600" />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-500 mb-1">Teaching Phase • Study Notes</p>
            <h2 className="text-2xl font-black text-gray-900 leading-tight">{topic?.title}</h2>
          </div>
        </div>

        {idata?.key_concepts?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {idata.key_concepts.map((c, i) => (
              <span key={i} className="text-xs font-bold px-4 py-2 rounded-xl bg-blue-50 text-blue-700 border border-blue-100 shadow-sm">
                #{c}
              </span>
            ))}
          </div>
        )}

        <div className="prose prose-blue max-w-none">
          {paragraphs.map((p, i) => (
            <p key={i} className="text-gray-700 leading-relaxed text-base mb-6 font-medium">
              {p}
            </p>
          ))}
        </div>

        <div className="mt-10 p-6 bg-white rounded-3xl border-2 border-dashed border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={18} className="text-indigo-500" />
            <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">My Personal Notes</h3>
          </div>
          <textarea
            value={userNote || ''}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder="Write down key takeaways, formulas, or reminders here..."
            className="w-full h-40 p-4 rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-indigo-400 outline-none text-sm font-medium text-gray-700 resize-none placeholder:text-gray-300"
          />
        </div>
      </div>

      <div className="pt-4 border-t border-gray-100">
        <button
          onClick={onStartAssignment}
          className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black py-5 rounded-2xl shadow-xl shadow-blue-100 hover:scale-[1.02] active:scale-[0.98] transition-all"
        >
          <Zap size={20} />
          Mastered the Notes? Start Assignment
        </button>
      </div>
    </motion.div>
  );
};

// ─── Assignment Panel ────────────────────────────────────────────────────────
const AssignmentPanel = ({ topic, onComplete, student }) => {
    const [submission, setSubmission] = useState('');
    const [grading, setGrading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const adata = topic?.assignment_data ? JSON.parse(topic.assignment_data) : null;

    const handleSubmit = async () => {
        if (!submission.trim()) return;
        setGrading(true);
        try {
            notifyInfo("Evaluating your submission with the Professional Rubric...");
            const response = await axios.post(`${BASE_URL}/api/students/peermate/chat`, {
                message: `I have completed the assignment: "${submission}". Please evaluate it strictly as a 10+ year veteran teacher. Return your evaluation in the standard JSON format.`,
                topicId: topic.id,
                studentId: student.id,
                course: topic.course_name
            });
            if (response.data?.success) {
                const evalData = response.data.evaluation;
                setFeedback(evalData);
                if (evalData.score >= 70) {
                    notifySuccess(`High Mastery Detected: ${evalData.score}%! You've unlocked the quiz.`);
                } else {
                    notifyInfo(`Mastery Score: ${evalData.score}%. Review the feedback to improve.`);
                }
            }
        } catch (e) {
            console.error('Grading error:', e);
            notifyError("Evaluation engine timeout. Please check your connection and try again.");
        } finally {
            setGrading(false);
        }
    };

    if (!adata) return <div className="p-8 text-center bg-white rounded-3xl">No assignment found for this topic.</div>;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col h-full gap-6">
            <div className="flex-1 overflow-y-auto space-y-6 pr-2 custom-scrollbar">
                <div className="p-6 bg-indigo-50 rounded-3xl border border-indigo-100">
                    <div className="flex items-center gap-2 mb-3">
                        <ClipboardList className="text-indigo-600" size={20} />
                        <h2 className="text-sm font-black text-indigo-900 uppercase tracking-widest">Active Assignment: {adata.type}</h2>
                    </div>
                    <p className="text-lg font-bold text-gray-800 leading-snug">{adata.task}</p>
                    <div className="mt-4 pt-4 border-t border-indigo-200">
                        <p className="text-[10px] font-black text-indigo-400 uppercase">Learning Goal</p>
                        <p className="text-xs font-bold text-indigo-700">{adata.learning_goal}</p>
                    </div>
                </div>

                {!feedback ? (
                    <div className="space-y-4">
                        <h3 className="text-sm font-black text-gray-900 uppercase">Your Submission</h3>
                        <textarea
                            value={submission}
                            onChange={(e) => setSubmission(e.target.value)}
                            placeholder="Type your answer, solution, or creative response here..."
                            className="w-full h-64 p-6 rounded-3xl bg-white border-2 border-gray-100 focus:border-blue-400 outline-none text-gray-700 font-medium transition-all"
                        />
                    </div>
                ) : (
                    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="p-6 bg-white rounded-3xl border-2 border-blue-50 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-black text-gray-900">AI Teacher's Evaluation</h3>
                            <div className="px-4 py-2 rounded-xl bg-blue-600 text-white font-black text-lg">
                                {feedback.accuracy}%
                            </div>
                        </div>
                        <p className="text-gray-700 font-medium leading-relaxed">{feedback.feedback}</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-100">
                                <p className="text-[9px] font-black text-emerald-500 uppercase mb-1">Depth</p>
                                <div className="h-1.5 w-full bg-emerald-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-emerald-500" style={{ width: `${feedback.depth}%` }} />
                                </div>
                            </div>
                            <div className="p-3 rounded-2xl bg-amber-50 border border-amber-100">
                                <p className="text-[9px] font-black text-amber-500 uppercase mb-1">Understanding</p>
                                <div className="h-1.5 w-full bg-amber-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-amber-500" style={{ width: `${feedback.understanding}%` }} />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>

            <div className="pt-4 border-t border-gray-100">
                {!feedback ? (
                    <button
                        onClick={handleSubmit}
                        disabled={grading || !submission.trim()}
                        className="w-full flex items-center justify-center gap-3 bg-gray-900 text-white font-black py-5 rounded-2xl shadow-xl hover:bg-black transition-all disabled:opacity-50"
                    >
                        {grading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send size={20} />}
                        {grading ? 'Evaluating Submission...' : 'Submit to PeerMate'}
                    </button>
                ) : (
                    <button
                        onClick={() => onComplete(feedback.accuracy)}
                        className="w-full flex items-center justify-center gap-3 bg-blue-600 text-white font-black py-5 rounded-2xl shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all"
                    >
                        <Zap size={20} />
                        Continue to Topic Quiz
                    </button>
                )}
            </div>
        </motion.div>
    );
};

// ─── Stage Map Overview ───────────────────────────────────────────────────────
const StageOverview = ({ curriculum, activeTopic, onSelectTopic, loadingSyllabus, onReset }) => (
  <motion.div key="map-overview" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
    <div className="mb-6 flex items-center justify-between">
      <div>
        <h2 className="text-xl font-black text-gray-900 mb-1">Your Mastery Path</h2>
        <p className="text-sm text-gray-400">Master each stage (70%+) to unlock the next. Fight the Forgetting Curve.</p>
      </div>
      <button 
        onClick={() => onReset()} 
        className="text-[10px] font-black uppercase tracking-widest text-red-400 hover:text-red-600 transition-colors px-3 py-1.5 rounded-lg border border-red-50 hover:bg-red-50"
      >
        Reset Path
      </button>
    </div>

    {loadingSyllabus ? (
      <div className="flex flex-col gap-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 rounded-2xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    ) : (
      <div className="flex flex-col gap-3">
        {curriculum.map((item, index) => {
          // Strict Gating: Must have 70% score on previous topic to unlock next
          const prevScore = index === 0 ? 100 : curriculum[index - 1]?.final_score || 0;
          const unlocked = prevScore >= 70;
          
          const isActive = activeTopic?.id === item.id;
          const isDone = Boolean(item.covered);

          // Retention Logic (Phase 3)
          const isDue = item.next_retention_date && new Date(item.next_retention_date) < new Date();

          const typeColors = {
            topic: { bg: isDone ? '#f0fdf4' : isActive ? '#eff6ff' : '#fff', border: isDone ? '#86efac' : isActive ? '#93c5fd' : '#e5e7eb', icon: isDone ? '#22c55e' : isActive ? '#2563eb' : '#9ca3af' },
            test:  { bg: isDone ? '#f0fdf4' : isActive ? '#f5f3ff' : '#fff', border: isDone ? '#86efac' : isActive ? '#a78bfa' : '#e5e7eb', icon: isDone ? '#22c55e' : isActive ? '#7c3aed' : '#9ca3af' },
            exam:  { bg: isDone ? '#f0fdf4' : isActive ? '#fffbeb' : '#fff', border: isDone ? '#86efac' : isActive ? '#fbbf24' : '#e5e7eb', icon: isDone ? '#22c55e' : isActive ? '#b45309' : '#9ca3af' },
          };
          const tc = typeColors[item.type] || typeColors.topic;
          const Icon = isDue ? Zap : isDone ? Sparkles : item.type === 'test' ? ClipboardList : item.type === 'exam' ? Trophy : BookOpen;

          return (
            <motion.button
              key={item.id}
              whileTap={unlocked ? { scale: 0.98 } : {}}
              onClick={() => unlocked && onSelectTopic(item)}
              disabled={!unlocked}
              className={`w-full text-left flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${isDue ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}
              style={{ background: unlocked ? tc.bg : '#f9fafb', borderColor: unlocked ? (isDue ? '#fbbf24' : tc.border) : '#f3f4f6', opacity: unlocked ? 1 : 0.5 }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: unlocked ? `${tc.icon}15` : '#f3f4f6' }}>
                {unlocked
                  ? <Icon size={18} style={{ color: isDue ? '#d97706' : tc.icon }} />
                  : <Lock size={16} className="text-gray-400" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: tc.icon }}>
                    {item.type} {index + 1}
                  </span>
                  {isDone && <span className="text-[9px] font-black text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Score: {Math.round(item.final_score)}%</span>}
                  {isDue && <span className="text-[9px] font-black text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full animate-pulse">! Review Due</span>}
                  {isActive && !isDone && <span className="text-[9px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">● Active</span>}
                </div>
                <p className="font-bold text-gray-900 text-sm truncate">{item.title}</p>
              </div>
              <div className="shrink-0 text-gray-400">
                {isDone ? <Sparkles size={16} className="text-green-500" /> : unlocked ? '→' : <Lock size={14} />}
              </div>
            </motion.button>
          );
        })}
      </div>
    )}
  </motion.div>
);

// ─── Chat Panel ───────────────────────────────────────────────────────────────
const ChatPanel = ({ messages, isTyping, onSend, activeTopic }) => {
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, isTyping]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(input);
    setInput('');
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const fd = new FormData();
        fd.append('file', blob, 'recording.wav');
        try {
          const res = await fetch(`${BASE_URL}/api/ai/transcribe`, { method: 'POST', body: fd });
          const data = await res.json();
          if (data.success) setInput(data.text);
        } catch {}
      };
      mr.start();
      setIsRecording(true);
    } catch { alert('Mic access denied.'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
    }
  };

  return (
    <div className="flex flex-col h-full">
      {activeTopic && (
        <div className="mb-3 px-1">
          <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">
            Asking about: {activeTopic.title}
          </p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-4 custom-scrollbar pr-1 pb-2">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-center">
            <MessageSquare size={32} className="text-gray-200 mb-3" />
            <p className="text-sm text-gray-400 font-medium">Ask PeerMate anything about this topic</p>
          </div>
        )}
        {messages.map(msg => (
          <motion.div
            key={msg.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
              msg.sender === 'user'
                ? 'bg-blue-600 text-white rounded-tr-sm'
                : 'bg-white border border-gray-100 text-gray-800 rounded-tl-sm shadow-sm'
            }`}>
              {msg.text}
              {msg.sender === 'bot' && msg.options?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {msg.options.map((opt, i) => (
                    <button key={i} onClick={() => onSend(opt)} className="text-xs font-bold px-3 py-1.5 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 hover:bg-indigo-600 hover:text-white transition-all">
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}
        {isTyping && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-1.5 items-center p-3 bg-white border rounded-2xl rounded-tl-sm w-16 shadow-sm">
            {[...Array(3)].map((_, i) => (
              <motion.div key={i} className="w-2 h-2 rounded-full bg-blue-400"
                animate={{ y: [0, -6, 0] }} transition={{ duration: 0.6, delay: i * 0.15, repeat: Infinity }} />
            ))}
          </motion.div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="mt-3 flex items-center gap-2 bg-white border-2 border-gray-100 rounded-2xl p-2 shadow-sm">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Ask PeerMate..."
          className="flex-1 px-3 py-2 outline-none text-sm font-medium text-gray-800 bg-transparent"
        />
        <button onClick={isRecording ? stopRecording : startRecording}
          className={`p-2 rounded-xl transition-all ${isRecording ? 'bg-red-50 text-red-500' : 'text-gray-400 hover:text-blue-500'}`}>
          {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
        </button>
        <button onClick={handleSend} disabled={!input.trim()}
          className="bg-blue-600 disabled:bg-gray-200 text-white w-10 h-10 rounded-xl flex items-center justify-center hover:bg-blue-700 transition-all">
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PeerMateUI = () => {
  const location = useLocation();
  const { course = 'Your Course', focus = 'General Learning', examContext = null, avatar = '/e007f099a7f5f305a19330ae4be1e77c82513a49.png' } = location.state || {};

  const [mode, setMode] = useState(MODE_MAP);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [usageState, setUsageState] = useState(null);
  const [remainingMs, setRemainingMs] = useState(FREE_DURATION_MS);
  const [curriculum, setCurriculum] = useState([]);
  const [activeTopic, setActiveTopic] = useState(null);
  const [loadingSyllabus, setLoadingSyllabus] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [studentProfile, setStudentProfile] = useState(null);
  const [userNotes, setUserNotes] = useState(() => {
    try {
      const studentId = JSON.parse(localStorage.getItem('student') || '{}').id;
      return JSON.parse(localStorage.getItem(`peermate_notes_${studentId}`) || '{}');
    } catch {
      return {};
    }
  });
  const audioRef = useRef(null);

  const student = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('student') || 'null'); } catch { return null; }
  }, []);

  // ── Usage Timer ──
  const refreshRemainingTime = useCallback((session) => {
    if (!session?.sessionStart) { setRemainingMs(FREE_DURATION_MS); return; }
    setRemainingMs(Math.max(0, (session.sessionStart + FREE_DURATION_MS) - Date.now()));
  }, []);

  const loadOrCreateUsageSession = useCallback(() => {
    const now = Date.now();
    let session = null;
    try { session = JSON.parse(localStorage.getItem(FREE_USAGE_KEY) || 'null'); } catch {}
    if (!session || now - session.windowStart > FREE_WINDOW_MS) {
      session = { windowStart: now, sessionStart: now };
      localStorage.setItem(FREE_USAGE_KEY, JSON.stringify(session));
    }
    if (!session.sessionStart) { session.sessionStart = now; localStorage.setItem(FREE_USAGE_KEY, JSON.stringify(session)); }
    setUsageState(session);
    refreshRemainingTime(session);
    return session;
  }, [refreshRemainingTime]);

  useEffect(() => {
    localStorage.setItem(`peermate_notes_${student?.id}`, JSON.stringify(userNotes));
  }, [userNotes, student?.id]);

  useEffect(() => { return () => { audioRef.current?.pause(); audioRef.current = null; }; }, []);

  // ── Init Session ──
  useEffect(() => {
    const init = async () => {
      if (!student?.id) { setLoadingSyllabus(false); return; }
      try {
        setLoadingSyllabus(true);
        const subRes = await axios.get(`${BASE_URL}/api/students/subscriptions/me/${student.id}`);
        const active = Boolean(subRes.data?.data?.isActive);
        setSubscriptionActive(active);
        if (!active) loadOrCreateUsageSession();

        const syllRes = await axios.get(`${BASE_URL}/api/ai/syllabus/${student.id}/${course}`);
        
        const profileRes = await axios.get(`${BASE_URL}/api/students/profile/${student.id}`);
        if (profileRes.data?.data?.learning_profile) {
            setStudentProfile(JSON.parse(profileRes.data.data.learning_profile));
        }

        if (syllRes.data?.data?.length > 0) {
            const data = syllRes.data.data;
            setCurriculum(data);
            const firstUnfinished = data.find(t => !t.covered);
            setActiveTopic(firstUnfinished || data[0]);
            notifyInfo("Mastery Path loaded from memory.");
        } else {
          notifyInfo("Constructing your specialized 16-stage Mastery Path...");
          const genRes = await axios.post(`${BASE_URL}/api/ai/syllabus-suggestion`, { courseName: course, category: focus, studentId: student.id, examContext, saveToDb: true });
          if (genRes.data?.success) {
            const retryRes = await axios.get(`${BASE_URL}/api/ai/syllabus/${student.id}/${course}`);
            if (retryRes.data?.data?.length > 0) {
              setCurriculum(retryRes.data.data);
              setActiveTopic(retryRes.data.data[0]);
              notifySuccess("Syllabus generated! Foundations unlocked.");
            }
          }
        }
      } catch (err) {
        console.error('Init error:', err);
        notifyError("System Error: Failed to synchronize with Mastery Engine. Please refresh.");
      } finally {
        setLoadingSyllabus(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student?.id, course, focus, examContext]);

  // ── Timer tick ──
  useEffect(() => {
    if (subscriptionActive) return;
    const t = setInterval(() => { if (usageState) refreshRemainingTime(usageState); }, 1000);
    return () => clearInterval(t);
  }, [subscriptionActive, usageState, refreshRemainingTime]);

  // ── TTS ──
  const speak = useCallback(async (text) => {
    try {
      const res = await fetch(`${BASE_URL}/api/ai/speak`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) });
      const ct = res.headers.get('content-type');
      if (ct?.includes('application/json')) { const d = await res.json(); if (d.fallback) { window.speechSynthesis?.speak(new SpeechSynthesisUtterance(text)); return; } }
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      audioRef.current?.pause();
      audioRef.current = new Audio(URL.createObjectURL(blob));
      audioRef.current.play();
    } catch { window.speechSynthesis?.speak(new SpeechSynthesisUtterance(text)); }
  }, []);

  // ── Chat Send ──
  const handleSend = useCallback(async (msg) => {
    if (!msg?.trim() || isTyping) return;
    const userMessage = { id: Date.now(), sender: 'user', text: msg };
    setMessages(p => [...p, userMessage]);
    setIsTyping(true);
    try {
      const res = await fetch(`${BASE_URL}/api/students/peermate/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, topicId: activeTopic?.id, studentId: student?.id, course }),
      });
      const data = await res.json();
      if (data.success) {
        const bot = { id: Date.now() + 1, sender: 'bot', text: data.response, options: data.next_options || [] };
        setMessages(p => [...p, bot]);
        if (data.profile) setStudentProfile(data.profile);
        speak(data.response);
      }
    } catch (e) { console.error(e); }
    finally { setIsTyping(false); }
  }, [isTyping, activeTopic, student, course, speak]);

  // ── Select Topic ──
  const handleSelectTopic = async (topic) => {
    setActiveTopic(topic);
    setMode(MODE_TEACHING);
    setMessages([{ id: Date.now(), sender: 'bot', text: `Let's dive into "${topic.title}". Read the notes carefully, then take the quiz to unlock the next stage!` }]);
    
    // Auto-read logic (Teacher Mode)
    if (topic.content) speak(topic.content);

    // Sync mode to backend
    try {
      await axios.post(`${BASE_URL}/api/students/peermate/session/mode`, {
        studentId: student?.id,
        topicId: topic.id,
        mode: 'teaching'
      });

      // Hydration logic (Phase B)
      if (!topic.content) {
        setLoadingSyllabus(true);
        notifyInfo(`Expanding topic detail: ${topic.title}...`);
        const res = await axios.post(`${BASE_URL}/api/students/peermate/topic/hydrate`, {
            topicId: topic.id,
            studentId: student?.id
        });
        if (res.data?.success) {
            const htopic = res.data.data;
            setCurriculum(prev => prev.map(t => t.id === topic.id ? htopic : t));
            setActiveTopic(htopic);
            speak(htopic.content);
            notifySuccess("Lesson hydrated with Adaptive Recall.");
        }
      }
    } catch (e) { 
        console.error('Mode/Hydration sync error:', e); 
        notifyError("Context Expansion Failed. PeerMate is experiencing high load—please try again.");
    } finally {
        setLoadingSyllabus(false);
    }
  };

  const handleResetPath = async () => {
    if (!window.confirm("Are you sure? This will delete your current path and mastery progress for this course to generate a new specialized curriculum.")) return;
    try {
      setLoadingSyllabus(true);
      notifyInfo("Resetting your path. This will trigger a new specialized blueprint...");
      const genRes = await axios.post(`${BASE_URL}/api/ai/syllabus-suggestion`, { 
        courseName: course, 
        category: focus, 
        studentId: student.id, 
        examContext, 
        saveToDb: true 
      });
      if (genRes.data?.success) {
        const retryRes = await axios.get(`${BASE_URL}/api/ai/syllabus/${student.id}/${course}`);
        if (retryRes.data?.data?.length > 0) {
          setCurriculum(retryRes.data.data);
          setActiveTopic(retryRes.data.data[0]);
          setMode(MODE_MAP);
          notifySuccess("Fresh Mastery Path generated successfully.");
        }
      }
    } catch (e) { 
      notifyError("Blueprint Reset Failed. Please check your connection.");
    }
    finally { setLoadingSyllabus(false); }
  };

  // ── Quiz Complete ──
  const handleQuizComplete = async (score) => {
    if (!activeTopic) return;
    const passed = score >= 70;
    if (passed) {
      try {
        await axios.post(`${BASE_URL}/api/ai/syllabus/mark-covered`, { topicId: activeTopic.id });
        setCurriculum(prev => prev.map(t => t.id === activeTopic.id ? { ...t, covered: 1, mastery_score: score } : t));
      } catch (e) { console.error(e); }
      const nextIdx = curriculum.findIndex(t => t.id === activeTopic.id) + 1;
      if (nextIdx < curriculum.length) {
        setMode(MODE_MAP);
        speak(`Excellent! You've mastered ${activeTopic.title}. Moving forward.`);
      } else {
        speak('Congratulations! You have completed the entire course!');
        setMode(MODE_MAP);
      }
    } else {
      setMode(MODE_TEACHING);
      speak("Let's review the notes again before trying the quiz.");
    }
  };

  const coveredCount = curriculum.filter(t => t.covered).length;
  const progressPct = curriculum.length ? Math.round((coveredCount / curriculum.length) * 100) : 0;
  const idata = parseInteractiveData(activeTopic?.interactive_data);

  const navItems = [
    { id: MODE_MAP, label: 'Path', icon: <Award size={18} /> },
    { id: MODE_TEACHING, label: 'Notes', icon: <BookOpen size={18} />, disabled: !activeTopic },
    { id: MODE_ASSIGNMENT, label: 'Task', icon: <ClipboardList size={18} />, disabled: !activeTopic || !activeTopic.assignment_data },
    { id: MODE_QUIZ, label: 'Quiz', icon: <Zap size={18} />, disabled: !activeTopic || !idata?.questions?.length },
  ];

  return (
    <div className="flex h-screen bg-[#f0f4f8] font-sans text-gray-800 overflow-hidden">
      <PeerMateSidebar curriculum={curriculum} currentCourse={course} />

      <main className="flex-1 flex flex-col h-screen relative overflow-hidden">
        {/* ── Top Bar ── */}
        <header className="shrink-0 flex items-center justify-between bg-white border-b border-gray-100 px-5 py-3 z-20">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl overflow-hidden border-2 ${isTyping ? 'border-blue-400' : 'border-gray-200'}`}>
              <img 
                src={avatar} 
                alt="PeerMate" 
                className="w-full h-full object-cover" 
                onError={handleAvatarError}
              />
            </div>
            <div>
              <p className="font-black text-gray-900 text-sm leading-tight truncate max-w-[160px]">{course}</p>
              <p className="text-[10px] font-bold text-blue-500 uppercase">{focus}</p>
            </div>
            {studentProfile && (
              <div className="ml-4 px-3 py-1 rounded-lg bg-blue-50 border border-blue-100 flex items-center gap-2">
                <Trophy size={14} className="text-amber-500" />
                <span className="text-[10px] font-black uppercase text-blue-700 tracking-wider">
                  {studentProfile.accuracy >= 80 ? 'Master' : studentProfile.accuracy >= 60 ? 'Specialist' : studentProfile.accuracy >= 40 ? 'Apprentice' : 'Novice'}
                </span>
              </div>
            )}
            {curriculum.length > 0 && curriculum[0].learning_context && (
              <div className="hidden md:flex ml-2 px-3 py-1 rounded-lg bg-indigo-50 border border-indigo-100 items-center gap-1.5">
                <ShieldCheck size={14} className="text-indigo-600" />
                <span className="text-[10px] font-black uppercase text-indigo-700 tracking-wider">
                  {curriculum[0].learning_context} Mode
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!subscriptionActive && (
              <span className="hidden sm:flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                ⏱ {formatMs(remainingMs)} free
              </span>
            )}
            <Link to="/Videochat" state={{ avatar, course, focus }}>
              <button className="flex items-center gap-1.5 border border-blue-100 text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full text-xs font-bold hover:bg-blue-100 transition-all">
                <Video size={13} /> Video
              </button>
            </Link>
            <button className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-bold hover:bg-blue-700 transition-all">
              <Volume2 size={13} /> Voice
            </button>
          </div>
        </header>

        {/* ── Progress Bar ── */}
        <div className="shrink-0 px-5 pt-3 pb-2 bg-white border-b border-gray-50">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Mastery Progress</span>
            <span className="text-[10px] font-black text-blue-600">{coveredCount}/{curriculum.length} Stages • {progressPct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* ── Mastery Map Scroll ── */}
        {curriculum.length > 0 && (
          <div className="shrink-0 px-5 py-3 bg-white border-b border-gray-100">
            <MasteryMap curriculum={curriculum} activeTopic={activeTopic} onSelectTopic={handleSelectTopic} />
          </div>
        )}

        {/* ── Main Content + Chat Split ── */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* Content Panel */}
          <div className={`flex-1 overflow-y-auto p-5 custom-scrollbar transition-all duration-300 ${chatOpen ? 'w-3/5' : 'w-full'}`}>
            {/* Mode Nav Tabs */}
            <div className="flex gap-2 mb-5">
              {navItems.map(item => (
                <button
                  key={item.id}
                  disabled={item.disabled}
                  onClick={() => !item.disabled && setMode(item.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                    mode === item.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : item.disabled
                      ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {item.icon}{item.label}
                </button>
              ))}

              <div className="ml-auto">
                <button
                  onClick={() => setChatOpen(o => !o)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-indigo-50 text-indigo-600 border border-indigo-100 hover:bg-indigo-600 hover:text-white transition-all"
                >
                  <MessageSquare size={14} />
                  {chatOpen ? 'Hide Chat' : 'Ask PeerMate'}
                </button>
              </div>
            </div>

            {/* Active Topic Context */}
            {activeTopic && mode !== MODE_MAP && (
              <div className="flex items-center gap-2 mb-4">
                <button onClick={() => setMode(MODE_MAP)} className="text-blue-500 hover:text-blue-700 transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <span className="text-xs font-bold text-gray-400">
                  {activeTopic.type?.toUpperCase()} {curriculum.findIndex(t => t.id === activeTopic.id) + 1} — {activeTopic.title}
                </span>
              </div>
            )}

            <AnimatePresence mode="wait">
              {mode === MODE_MAP && (
                <StageOverview
                  key="map"
                  curriculum={curriculum}
                  activeTopic={activeTopic}
                  onSelectTopic={handleSelectTopic}
                  loadingSyllabus={loadingSyllabus}
                  onReset={handleResetPath}
                />
              )}

              {mode === MODE_TEACHING && activeTopic && (
                <TeachingPanel
                  key="teaching"
                  topic={activeTopic}
                  onStartAssignment={() => setMode(MODE_ASSIGNMENT)}
                  userNote={userNotes[activeTopic.id]}
                  onNoteChange={(val) => setUserNotes(p => ({ ...p, [activeTopic.id]: val }))}
                  hydrating={loadingSyllabus}
                />
              )}

              {mode === MODE_ASSIGNMENT && activeTopic && (
                <AssignmentPanel
                    key="assignment"
                    topic={activeTopic}
                    student={student}
                    onComplete={(score) => setMode(MODE_QUIZ)}
                />
              )}

              {mode === MODE_QUIZ && activeTopic && (
                <motion.div key="quiz" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="p-3 rounded-2xl bg-blue-100">
                      <ClipboardList size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-blue-500">Quiz</p>
                      <h2 className="text-xl font-black text-gray-900">{activeTopic.title}</h2>
                    </div>
                  </div>
                  <InteractiveQuiz
                    questions={idata?.questions || []}
                    topicTitle={activeTopic.title}
                    onComplete={handleQuizComplete}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Chat Side Panel */}
          <AnimatePresence>
            {chatOpen && (
              <motion.div
                initial={{ x: '100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '100%', opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="w-[380px] shrink-0 border-l border-gray-200 bg-white flex flex-col p-4 overflow-hidden"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <p className="font-black text-sm text-gray-900">PeerMate Chat</p>
                  </div>
                  <button onClick={() => setChatOpen(false)} className="text-gray-400 hover:text-gray-700 transition-colors">
                    <X size={18} />
                  </button>
                </div>
                <ChatPanel
                  messages={messages}
                  isTyping={isTyping}
                  onSend={handleSend}
                  activeTopic={activeTopic}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default PeerMateUI;
